import React, { useMemo, useState } from 'react';
import { StudioIcon, StudioIconName } from './StudioIcons';
import { GENRE_EXEMPLARS } from '../genreExemplars';

type Locale = 'zh' | 'en';

type StarterMode = 'blank' | 'preset' | 'load';
type PresetStepKey = 'genre' | 'engine' | 'tone' | 'format';

interface SnapshotItem {
  id: number | string;
  name?: string;
  created_at?: string;
}

export interface PresetFacets {
  choice_ids: Record<PresetStepKey, string>;
  choice_labels: Record<PresetStepKey, string>;
  search_tags: string[];
  preferred_pools?: string[];
}

export interface PresetSeed {
  worldview: string;
  themes: string;
  prompt: string;
  label: string;
  targetEpisodeCount?: number;
  dramaSeriesMode?: 'short' | 'long';
  presetFacets?: PresetFacets;
}

interface WorkspaceStarterModalProps {
  locale: Locale;
  open: boolean;
  busy?: boolean;
  snapshots: SnapshotItem[];
  loadingSnapshots?: boolean;
  onClose: () => void;
  onStartBlank: () => void;
  onApplyPreset: (seed: PresetSeed) => void;
  onLoadLatest: () => void;
  onLoadSavedScript: () => void;
  onLoadSnapshot: (snapshotId: number | string) => void;
}

interface OptionItem {
  id: string;
  icon: StudioIconName;
  label: { zh: string; en: string };
  note: { zh: string; en: string };
  worldview: { zh: string; en: string };
  themes: { zh: string[]; en: string[] };
  prompt: { zh: string; en: string };
  targetEpisodeCount?: number;
}

interface StepDefinition {
  key: PresetStepKey;
  title: { zh: string; en: string };
  hint: { zh: string; en: string };
  options: OptionItem[];
}

const PRESET_OPTION_TAGS: Record<string, string[]> = {
  urban_emotion: ['urban_emotion', 'urban_relationship', 'contemporary_city', 'intimacy', 'status_gap', 'family_drama'],
  suspense_reversal: ['suspense_reversal', 'suspense', 'mystery', 'reversal', 'secret_exposure', 'thriller'],
  family_power: ['family_power', 'family_drama', 'domestic_control', 'inheritance_pressure', 'status_order'],
  workplace_game: ['workplace_game', 'workplace_drama', 'alliance', 'reputation', 'status_contest'],
  identity_reversal: ['identity_reversal', 'hidden_identity', 'recognition_risk', 'status_reversal'],
  hidden_secret: ['hidden_secret', 'secret_exposure', 'reveal_ladder', 'trust_collapse', 'suspicion'],
  revenge_payoff: ['revenge_payoff', 'revenge', 'justice_payoff', 'payoff', 'reversal'],
  rescue_alliance: ['rescue_alliance', 'alliance', 'rescue', 'collective_action', 'trust'],
  restrained_realist: ['restrained_realist', 'realist_drama', 'naturalism', 'subtext'],
  high_hook: ['high_hook', 'short_drama', 'cliffhanger', 'fast_paced', 'hook_density'],
  poetic_literary: ['poetic_literary', 'literary_drama', 'subtext', 'public_domain_classic', 'ensemble_drama'],
  cold_noir: ['cold_noir', 'crime_thriller', 'moral_pressure', 'fatalism', 'suspense'],
  short_drama_80: ['short_drama', '80_episode', 'episode_template', 'hook_density', 'cliffhanger', 'fast_paced'],
  micro_series: ['micro_series', 'episodic_short_drama', 'short_drama', 'cliffhanger'],
  mini_series: ['mini_series', 'core_scene', 'chamber_drama', 'public_domain_classic'],
  feature_three_act: ['feature_three_act', 'feature_film', 'official_awards_screenplay', 'three_act'],
};

const PRESET_OPTION_POOLS: Partial<Record<string, string[]>> = {
  poetic_literary: ['public_domain_classic'],
  mini_series: ['public_domain_classic'],
  feature_three_act: ['official_awards_screenplay'],
};

const STEP_DEFINITIONS: StepDefinition[] = [
  {
    key: 'genre',
    title: { zh: '题材方向', en: 'Genre direction' },
    hint: { zh: '先确定故事的世界和人物关系底盘。', en: 'Start with the world and relationship base.' },
    options: [
      {
        id: 'urban_emotion',
        icon: 'character',
        label: { zh: '都市情感', en: 'Urban relationship' },
        note: { zh: '当代城市、亲密关系与阶层落差。', en: 'Contemporary city life, intimacy, and status gaps.' },
        worldview: {
          zh: '当代都市环境中，人物在亲密关系、体面与现实压力之间不断做出选择。',
          en: 'A contemporary city world where characters keep choosing between intimacy, dignity, and practical pressure.',
        },
        themes: {
          zh: ['亲密关系', '身份差距', '自我尊严'],
          en: ['intimacy', 'status gap', 'dignity'],
        },
        prompt: {
          zh: '突出人物关系拉扯与情绪递进，让冲突落在具体互动上。',
          en: 'Emphasize relational tension and emotional escalation through concrete interactions.',
        },
      },
      {
        id: 'suspense_reversal',
        icon: 'scene',
        label: { zh: '悬疑反转', en: 'Suspense reversal' },
        note: { zh: '秘密、误导与信息差驱动推进。', en: 'Secrets, misdirection, and information gaps drive the plot.' },
        worldview: {
          zh: '一个表面秩序稳定、实则层层隐藏信息的现实环境，真相需要通过事件推进逐步显露。',
          en: 'A seemingly stable world with layers of hidden information where truth emerges through advancing events.',
        },
        themes: {
          zh: ['秘密', '真相', '误导'],
          en: ['secret', 'truth', 'misdirection'],
        },
        prompt: {
          zh: '保持信息差与递进揭示，让每一场都兼顾推进和反转。',
          en: 'Preserve information asymmetry and progressive reveals so every scene advances and twists.',
        },
      },
      {
        id: 'family_power',
        icon: 'world',
        label: { zh: '家庭权力', en: 'Family power' },
        note: { zh: '家庭伦理、资源分配与情感控制。', en: 'Family ethics, resource allocation, and emotional control.' },
        worldview: {
          zh: '家庭与组织资源紧密绑定，私人情感与公共秩序互相牵制，任何选择都会牵动关系格局。',
          en: 'Family and institutional resources are tightly bound, so private emotions and public order constantly constrain each other.',
        },
        themes: {
          zh: ['家庭秩序', '控制', '代价'],
          en: ['family order', 'control', 'cost'],
        },
        prompt: {
          zh: '强化家庭内部的权力交换与情感绑架，推动角色在忠诚与自救之间做决定。',
          en: 'Highlight power exchanges and emotional coercion inside the family, forcing choices between loyalty and self-preservation.',
        },
      },
      {
        id: 'workplace_game',
        icon: 'board',
        label: { zh: '职场博弈', en: 'Workplace game' },
        note: { zh: '利益、联盟、声誉与位置竞争。', en: 'Interests, alliances, reputation, and positional conflict.' },
        worldview: {
          zh: '组织内部层级鲜明，资源流动与声誉判断高度影响人物行动与结局。',
          en: 'A sharply stratified organization where resources and reputation strongly shape action and outcome.',
        },
        themes: {
          zh: ['利益', '联盟', '位置争夺'],
          en: ['interest', 'alliance', 'status contest'],
        },
        prompt: {
          zh: '让人物目标更明确，把冲突写成可执行的策略选择与公开博弈。',
          en: 'Make goals explicit and write conflict as strategic choices and visible contests.',
        },
      },
    ],
  },
  {
    key: 'engine',
    title: { zh: '叙事引擎', en: 'Narrative engine' },
    hint: { zh: '确定这部剧主要靠什么机制驱动。', en: 'Choose the main mechanism that drives the story.' },
    options: [
      {
        id: 'identity_reversal',
        icon: 'device',
        label: { zh: '身份反转', en: 'Identity reversal' },
        note: { zh: '核心信息在后续场次形成身份翻转。', en: 'A core reveal flips status and identity later on.' },
        worldview: {
          zh: '角色的社会身份与真实能力之间存在显著错位，迟来的揭示将改变全部关系结构。',
          en: 'There is a sharp gap between social identity and real capability, and a late reveal changes the entire relationship structure.',
        },
        themes: {
          zh: ['身份', '误判', '翻转'],
          en: ['identity', 'misjudgment', 'reversal'],
        },
        prompt: {
          zh: '埋设早期误判与延迟揭示，让回收形成情绪与地位的双重翻转。',
          en: 'Plant early misreadings and delayed reveals so the payoff flips both emotion and status.',
        },
      },
      {
        id: 'hidden_secret',
        icon: 'inspector',
        label: { zh: '秘密曝光', en: 'Secret exposure' },
        note: { zh: '信息差不断缩小并引发关系崩塌。', en: 'Information gaps close and destabilize relationships.' },
        worldview: {
          zh: '人物维持表面平衡的前提是某个秘密尚未暴露，而暴露过程会逐层扩大风险。',
          en: 'The surface balance depends on a secret remaining hidden, and exposure expands risk layer by layer.',
        },
        themes: {
          zh: ['秘密', '暴露', '信任'],
          en: ['secret', 'exposure', 'trust'],
        },
        prompt: {
          zh: '让每一轮揭示都带来新的站队与代价，不只是单次信息公开。',
          en: 'Make each reveal trigger new alignments and costs rather than a one-off disclosure.',
        },
      },
      {
        id: 'revenge_payoff',
        icon: 'arc',
        label: { zh: '复仇回收', en: 'Revenge payoff' },
        note: { zh: '目标明确，装置与铺垫服务最终回收。', en: 'Clear target, with devices and setups serving a final payoff.' },
        worldview: {
          zh: '角色长期承受压制或羞辱，故事结构围绕反制、回收与代价组织。',
          en: 'Characters endure long-term pressure or humiliation, and the structure is organized around counteraction, payoff, and cost.',
        },
        themes: {
          zh: ['复仇', '回收', '代价'],
          en: ['revenge', 'payoff', 'cost'],
        },
        prompt: {
          zh: '明确铺垫与回收的对应关系，让转折服务终局而不是碎片化刺激。',
          en: 'Keep setups and payoffs explicitly paired so turns serve the ending rather than fragmentary shocks.',
        },
      },
      {
        id: 'rescue_alliance',
        icon: 'structure',
        label: { zh: '结盟救援', en: 'Alliance and rescue' },
        note: { zh: '人物通过结盟跨过更大困境。', en: 'Characters cross a larger crisis by forming alliances.' },
        worldview: {
          zh: '单个角色无法独立脱困，联盟关系与信任交换构成推进核心。',
          en: 'No single character can escape alone, so alliance and trust exchange become the main engine.',
        },
        themes: {
          zh: ['结盟', '信任', '拯救'],
          en: ['alliance', 'trust', 'rescue'],
        },
        prompt: {
          zh: '让关系从试探到协作再到承担风险，形成真正的联合行动线。',
          en: 'Move relationships from testing to cooperation to shared risk so the alliance becomes a real action line.',
        },
      },
    ],
  },
  {
    key: 'tone',
    title: { zh: '表达风格', en: 'Expression style' },
    hint: { zh: '决定语言和节奏的表层风格。', en: 'Choose the surface style of language and pacing.' },
    options: [
      {
        id: 'restrained_realist',
        icon: 'quill',
        label: { zh: '克制写实', en: 'Restrained realist' },
        note: { zh: '对白简洁，情绪逐步上浮。', en: 'Lean dialogue with gradually surfacing emotion.' },
        worldview: { zh: '', en: '' },
        themes: { zh: [], en: [] },
        prompt: {
          zh: '语言克制、动作明确、情绪不过度外放，让力量来自人物关系和情境。',
          en: 'Keep language restrained and action precise so force comes from relationships and situation.',
        },
      },
      {
        id: 'high_hook',
        icon: 'spark',
        label: { zh: '强钩子短剧', en: 'High-hook short drama' },
        note: { zh: '快节奏、强反应、每场都要推进。', en: 'Fast pace, strong reactions, every scene must move.' },
        worldview: { zh: '', en: '' },
        themes: { zh: [], en: [] },
        prompt: {
          zh: '提高钩子密度和节奏效率，让每场都有推进、转折或回收。',
          en: 'Increase hook density and pacing efficiency so every scene advances, twists, or pays off.',
        },
      },
      {
        id: 'poetic_literary',
        icon: 'dialogue',
        label: { zh: '文艺留白', en: 'Poetic restraint' },
        note: { zh: '更重气氛、潜台词与余味。', en: 'More atmosphere, subtext, and lingering aftertaste.' },
        worldview: { zh: '', en: '' },
        themes: { zh: [], en: [] },
        prompt: {
          zh: '保留停顿、含混和潜台词，让人物情绪通过环境与动作折射出来。',
          en: 'Preserve pauses, ambiguity, and subtext so emotion refracts through setting and gesture.',
        },
      },
      {
        id: 'cold_noir',
        icon: 'canvas',
        label: { zh: '冷峻黑色', en: 'Cold noir' },
        note: { zh: '压力、秩序和危险感更强。', en: 'Stronger pressure, structure, and danger.' },
        worldview: { zh: '', en: '' },
        themes: { zh: [], en: [] },
        prompt: {
          zh: '保持冷静、压迫和不确定感，让冲突像逐步收紧的绞索。',
          en: 'Maintain calm, pressure, and uncertainty so conflict feels like a tightening noose.',
        },
      },
    ],
  },
  {
    key: 'format',
    title: { zh: '成片尺度', en: 'Delivery scale' },
    hint: { zh: '给系统一个更清晰的结构长度预期。', en: 'Give the system a clearer expected scale.' },
    options: [
      {
        id: 'short_drama_8',
        icon: 'spark',
        label: { zh: '8 集短剧验收', en: '8-episode short drama' },
        note: { zh: '适合完整验证逐集钩子、升级、回收和阶段证据。', en: 'Designed to validate per-episode hooks, escalation, payoff, and stage evidence.' },
        targetEpisodeCount: 8,
        worldview: { zh: '', en: '' },
        themes: { zh: [], en: [] },
        prompt: {
          zh: '生成 8 集竖屏短剧，每集 1–3 分钟；每集必须有独立行动目标、阻力、状态变化和结尾追看钩子，伏笔与反转按因果逐步铺设并回收。',
          en: 'Generate an 8-episode vertical short drama, 1–3 minutes per episode, with a distinct objective, opposition, state change, and cliffhanger in every episode.',
        },
      },
      {
        id: 'short_drama_80',
        icon: 'spark',
        label: { zh: '80 集短剧', en: '80-episode short drama' },
        note: { zh: '按短剧模板组织，适配长链路追更。', en: 'Organized as a short-drama template across a longer episode run.' },
        targetEpisodeCount: 80,
        worldview: { zh: '', en: '' },
        themes: { zh: [], en: [] },
        prompt: {
          zh: '目标为 80 集短剧：每集保持开场钩子 / 中点翻转 / 结尾悬点的节奏模板，持续追更。',
          en: 'Target a 80-episode short drama with a consistent hook / midpoint shift / cliffhanger template per episode.',
        },
      },
      {
        id: 'micro_series',
        icon: 'board',
        label: { zh: '15-20 集短剧', en: '15-20 episode short drama' },
        note: { zh: '适合强钩子和阶段性回收。', en: 'Best for strong hooks and staged payoffs.' },
        targetEpisodeCount: 18,
        worldview: { zh: '', en: '' },
        themes: { zh: [], en: [] },
        prompt: {
          zh: '按短剧节奏组织结构，保持更快的起势、更密的事件与更明显的阶段性回收。',
          en: 'Organize the structure with short-drama pacing, faster lift-off, denser events, and clearer staged payoffs.',
        },
      },
      {
        id: 'mini_series',
        icon: 'lanes',
        label: { zh: '6 场核心戏', en: '6 core-scene mini drama' },
        note: { zh: '收束更紧，强调关键场次。', en: 'More compact, focused on essential scenes.' },
        targetEpisodeCount: 6,
        worldview: { zh: '', en: '' },
        themes: { zh: [], en: [] },
        prompt: {
          zh: '压缩成少量核心场景，保证每场都承担明确的结构职责。',
          en: 'Compress the draft into a small set of core scenes and make each scene carry a clear structural duty.',
        },
      },
      {
        id: 'feature_three_act',
        icon: 'script',
        label: { zh: '电影式三幕', en: 'Feature-style three acts' },
        note: { zh: '更重完整弧线与场景层递进。', en: 'Leans toward fuller arcs and scene-level progression.' },
        targetEpisodeCount: 9,
        worldview: { zh: '', en: '' },
        themes: { zh: [], en: [] },
        prompt: {
          zh: '采用更完整的三幕推进，让人物弧线和场景推进都更稳定、更连贯。',
          en: 'Use a fuller three-act progression so character arcs and scene progression remain stable and coherent.',
        },
      },
    ],
  },
];

const MODE_COPY = {
  zh: {
    title: '开始创建',
    subtitle: '把启动方式单独拎出来，不再让生成入口藏在工作台里。',
    blank: '从空白开始',
    preset: '预设向导',
    load: '载入已保存',
    blankNote: '创建一份空白草稿，手动搭建人物、场景与装置。',
    presetNote: '一步一步选择题材、叙事引擎、风格和尺度，再进入编辑。',
    loadNote: '继续上一次工作区，或直接载入已保存剧本。',
    blankAction: '创建空白草稿',
    presetAction: '应用预设并继续编辑',
    loadLatest: '载入最近快照',
    loadScript: '载入已保存剧本',
    emptySnapshots: '当前还没有已保存快照。',
    recentSnapshots: '最近快照',
    loadThis: '载入此快照',
    previous: '上一步',
    next: '下一步',
    chooseAll: '请按顺序完成每一步。',
    presetIntro: '系统会先给出一个可编辑的结构种子，你仍然可以在生成前继续改世界观、主题词和写作指令。',
    applyHint: '应用后会自动打开“生成结构”面板，方便你继续修改后再生成。',
    readyPrefix: '当前方案：',
  },
  en: {
    title: 'Start',
    subtitle: 'Expose project start modes directly instead of hiding them inside the workspace.',
    blank: 'Start from scratch',
    preset: 'Preset wizard',
    load: 'Load saved work',
    blankNote: 'Create a blank draft and model characters, scenes, and devices manually.',
    presetNote: 'Choose genre, dramatic engine, style, and scale step by step before editing.',
    loadNote: 'Continue from the latest workspace or load a saved script directly.',
    blankAction: 'Create blank draft',
    presetAction: 'Apply preset and continue editing',
    loadLatest: 'Load latest snapshot',
    loadScript: 'Load saved script',
    emptySnapshots: 'There are no saved snapshots yet.',
    recentSnapshots: 'Recent snapshots',
    loadThis: 'Load snapshot',
    previous: 'Back',
    next: 'Next',
    chooseAll: 'Complete each step in order.',
    presetIntro: 'The wizard creates an editable structured seed. You can still refine worldview, themes, and prompt before generation.',
    applyHint: 'After applying, the structure modal opens so you can tweak it before generating.',
    readyPrefix: 'Current plan:',
  },
} as const;

const modeMeta = [
  { id: 'blank' as StarterMode, icon: 'quill' as StudioIconName },
  { id: 'preset' as StarterMode, icon: 'structure' as StudioIconName },
  { id: 'load' as StarterMode, icon: 'library' as StudioIconName },
];

const formatSnapshotTime = (value?: string) => {
  if (!value) {
    return '';
  }
  return value.replace('T', ' ').slice(0, 16);
};

const WorkspaceStarterModal: React.FC<WorkspaceStarterModalProps> = ({
  locale,
  open,
  busy,
  snapshots,
  loadingSnapshots,
  onClose,
  onStartBlank,
  onApplyPreset,
  onLoadLatest,
  onLoadSavedScript,
  onLoadSnapshot,
}) => {
  const text = MODE_COPY[locale];
  const [mode, setMode] = useState<StarterMode>('preset');
  const [stepIndex, setStepIndex] = useState(0);
  const [choices, setChoices] = useState<Record<PresetStepKey, string>>({
    genre: STEP_DEFINITIONS[0].options[0].id,
    engine: STEP_DEFINITIONS[1].options[0].id,
    tone: 'high_hook',
    format: 'short_drama_80',
  });

  const activeStep = STEP_DEFINITIONS[stepIndex];

  const selectedOptions = useMemo(() => {
    const picked = {} as Record<PresetStepKey, OptionItem>;
    STEP_DEFINITIONS.forEach((step) => {
      const option = step.options.find((item) => item.id === choices[step.key]) || step.options[0];
      picked[step.key] = option;
    });
    return picked;
  }, [choices]);

  const presetSeed = useMemo(() => {
    const worldview = STEP_DEFINITIONS.map((step) => selectedOptions[step.key]?.worldview?.[locale] || '')
      .filter(Boolean)
      .join(locale === 'zh' ? ' ' : ' ');
    const themes = STEP_DEFINITIONS.flatMap((step) => selectedOptions[step.key]?.themes?.[locale] || [])
      .filter(Boolean)
      .join(locale === 'zh' ? '，' : ', ');
    const genreExemplar = GENRE_EXEMPLARS[selectedOptions.genre?.id || ''];
    const prompt = STEP_DEFINITIONS.map((step) => selectedOptions[step.key]?.prompt?.[locale] || '')
      .filter(Boolean)
      .join(' ') + (genreExemplar
        ? (locale === 'zh'
          ? ` 参考${genreExemplar.work.zh}的关系机制：${genreExemplar.mechanism.zh} 原创改写：${genreExemplar.transform.zh}`
          : ` Use ${genreExemplar.work.en} as a relationship mechanism: ${genreExemplar.mechanism.en} Original transformation: ${genreExemplar.transform.en}`)
        : '');
    const label = STEP_DEFINITIONS.map((step) => selectedOptions[step.key]?.label?.[locale] || '')
      .filter(Boolean)
      .join(locale === 'zh' ? ' / ' : ' / ');
    const targetEpisodeCount = selectedOptions.format?.targetEpisodeCount || 0;
    const choiceIds = {} as Record<PresetStepKey, string>;
    const choiceLabels = {} as Record<PresetStepKey, string>;
    const searchTags = new Set<string>();
    const preferredPools = new Set<string>();

    STEP_DEFINITIONS.forEach((step) => {
      const option = selectedOptions[step.key];
      const optionId = option?.id || '';
      const optionLabel = option?.label?.[locale] || '';
      choiceIds[step.key] = optionId;
      choiceLabels[step.key] = optionLabel;
      searchTags.add(step.key);
      if (optionId) {
        searchTags.add(optionId);
      }
      if (optionLabel) {
        searchTags.add(optionLabel);
      }
      (PRESET_OPTION_TAGS[optionId] || []).forEach((tag) => searchTags.add(tag));
      (PRESET_OPTION_POOLS[optionId] || []).forEach((pool) => preferredPools.add(pool));
    });

    return {
      worldview,
      themes,
      prompt,
      label,
      targetEpisodeCount,
      dramaSeriesMode: (selectedOptions.format?.id === 'feature_three_act' ? 'long' : 'short') as 'short' | 'long',
      presetFacets: {
        choice_ids: choiceIds,
        choice_labels: choiceLabels,
        search_tags: Array.from(searchTags).filter(Boolean),
        preferred_pools: Array.from(preferredPools).filter(Boolean),
      },
    };
  }, [locale, selectedOptions]);

  if (!open) {
    return null;
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card modal-card--starter" onClick={(event) => event.stopPropagation()}>
        <div className="modal-card__header">
          <div className="panel-title">
            <span className="panel-title__icon">
              <StudioIcon name="spark" />
            </span>
            <div>
              <div className="panel-card__eyebrow">{text.title}</div>
              <h2>{text.subtitle}</h2>
            </div>
          </div>
          <button className="modal-close" type="button" onClick={onClose}>
            {locale === 'zh' ? '关闭' : 'Close'}
          </button>
        </div>

        <div className="starter-mode-grid">
          {modeMeta.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`starter-mode-card ${mode === item.id ? 'is-active' : ''}`}
              onClick={() => setMode(item.id)}
            >
              <span className="starter-mode-card__icon">
                <StudioIcon name={item.icon} />
              </span>
              <strong>{text[item.id]}</strong>
              <span>
                {item.id === 'blank'
                  ? text.blankNote
                  : item.id === 'preset'
                  ? text.presetNote
                  : text.loadNote}
              </span>
            </button>
          ))}
        </div>

        {mode === 'blank' && (
          <div className="starter-panel">
            <p className="starter-copy">{text.blankNote}</p>
            <button className="app-button app-button--strong" type="button" onClick={onStartBlank} disabled={busy}>
              <span className="app-button__content">
                <StudioIcon name="quill" />
                <span>{text.blankAction}</span>
              </span>
            </button>
          </div>
        )}

        {mode === 'preset' && (
          <div className="starter-panel">
            <p className="starter-copy">{text.presetIntro}</p>
            <div className="starter-stepper">
              {STEP_DEFINITIONS.map((step, index) => (
                <button
                  key={step.key}
                  type="button"
                  className={`starter-stepper__item ${index === stepIndex ? 'is-active' : ''}`}
                  onClick={() => setStepIndex(index)}
                >
                  <span>{index + 1}</span>
                  <strong>{step.title[locale]}</strong>
                </button>
              ))}
            </div>

            <div className="starter-step-card">
              <div className="starter-step-card__header">
                <div>
                  <div className="panel-card__eyebrow">{activeStep.title[locale]}</div>
                  <h3>{activeStep.hint[locale]}</h3>
                </div>
              </div>
              <div className="starter-option-grid">
                {activeStep.options.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`starter-option ${choices[activeStep.key] === option.id ? 'is-active' : ''}`}
                    onClick={() =>
                      setChoices((current) => ({
                        ...current,
                        [activeStep.key]: option.id,
                      }))
                    }
                  >
                    <span className="starter-option__icon">
                      <StudioIcon name={option.icon} />
                    </span>
                    <strong>{option.label[locale]}</strong>
                    <span>{option.note[locale]}</span>
                  </button>
                ))}
              </div>
              {activeStep.key === 'genre' && GENRE_EXEMPLARS[choices.genre] && (
                <div className="genre-exemplar">
                  <strong>{locale === 'zh' ? '参考范本' : 'Reference example'}：{GENRE_EXEMPLARS[choices.genre].work[locale]}</strong>
                  <p>{GENRE_EXEMPLARS[choices.genre].mechanism[locale]}</p>
                  <p>{GENRE_EXEMPLARS[choices.genre].transform[locale]}</p>
                  <a href={GENRE_EXEMPLARS[choices.genre].sourceUrl} target="_blank" rel="noreferrer">{locale === 'zh' ? '查看作品来源' : 'View source'} →</a>
                </div>
              )}
            </div>

            <div className="starter-summary">
              <strong>{text.readyPrefix}</strong>
              <span>{presetSeed.label || text.chooseAll}</span>
            </div>
            <p className="starter-copy starter-copy--muted">{text.applyHint}</p>

            <div className="starter-actions">
              <button
                className="app-button app-button--ghost"
                type="button"
                onClick={() => setStepIndex((current) => Math.max(0, current - 1))}
                disabled={stepIndex === 0}
              >
                {text.previous}
              </button>
              {stepIndex < STEP_DEFINITIONS.length - 1 ? (
                <button className="app-button" type="button" onClick={() => setStepIndex((current) => current + 1)}>
                  {text.next}
                </button>
              ) : (
                <button
                  className="app-button app-button--strong"
                  type="button"
                  onClick={() => onApplyPreset(presetSeed)}
                  disabled={busy}
                >
                  <span className="app-button__content">
                    <StudioIcon name="structure" />
                    <span>{text.presetAction}</span>
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {mode === 'load' && (
          <div className="starter-panel">
            <div className="starter-load-actions">
              <button className="starter-load-card" type="button" onClick={onLoadLatest} disabled={busy}>
                <span className="starter-load-card__icon">
                  <StudioIcon name="refresh" />
                </span>
                <strong>{text.loadLatest}</strong>
                <span>{text.loadNote}</span>
              </button>
              <button className="starter-load-card" type="button" onClick={onLoadSavedScript} disabled={busy}>
                <span className="starter-load-card__icon">
                  <StudioIcon name="script" />
                </span>
                <strong>{text.loadScript}</strong>
                <span>{locale === 'zh' ? '直接恢复已保存剧本及对应结构。' : 'Restore the saved script and its paired structure.'}</span>
              </button>
            </div>

            <div className="starter-snapshot-list">
              <div className="panel-card__eyebrow">{text.recentSnapshots}</div>
              {loadingSnapshots && <p className="starter-copy">{locale === 'zh' ? '正在读取快照...' : 'Loading snapshots...'}</p>}
              {!loadingSnapshots && snapshots.length === 0 && <p className="starter-copy">{text.emptySnapshots}</p>}
              {!loadingSnapshots &&
                snapshots.map((snapshot) => (
                  <div key={snapshot.id} className="starter-snapshot-row">
                    <div>
                      <strong>{snapshot.name || snapshot.id}</strong>
                      <span>{formatSnapshotTime(snapshot.created_at)}</span>
                    </div>
                    <button className="app-button app-button--ghost app-button--small" type="button" onClick={() => onLoadSnapshot(snapshot.id)}>
                      {text.loadThis}
                    </button>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkspaceStarterModal;
